/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import { useEffect, useState } from "react";
import cx from "classnames";
import { IListComponentItemProps } from "components/Configuration/ListComponent/components/ListComponentItem/models";
import {
  calcProgress,
  DataProvider,
  ImageHelper,
  RouteHelper,
  TimeHelper,
  useCanWatchAuthContent,
  useIsLoggedIn,
} from "@nf/common";
import dynamic from "next/dynamic";
import { useTranslation } from "next-i18next";

import PlayFilledIcon from "resources/play-filled.svg";

import styles from "./EpisodeListItem.module.scss";

const ImageWithPlaceholder = dynamic(
  () => import("components/ImageWithPlaceholder")
);

const PlayInfo = dynamic(() => import("components/PlayInfo"), { ssr: false });

const ProgressBar = dynamic(() => import("components/ProgressBar"), {
  ssr: false,
});

const LinkWithLocale = dynamic(() => import("components/LinkWithLocale"));

type EpisodeListItemProps = IListComponentItemProps;

export const EpisodeListItem = ({ media }: EpisodeListItemProps) => {
  const { t } = useTranslation("translations");
  const { canWatch } = useCanWatchAuthContent(media);

  const [progress, setProgress] = useState<undefined | number>(undefined);
  const isLoggedIn = useIsLoggedIn();
  const imageUrl =
    ImageHelper.getFrameImageUrl(media?.Images, media?.MediaTypeCode) ?? "";

  // TODO Fetch progress for all episodes with one
  // request if possible instead of a request per episode
  useEffect(() => {
    if (isLoggedIn && !media?.LastViewedOffset && media && media.IsPlayable) {
      DataProvider.getMediaProgress(media).then((progress) => {
        setProgress(calcProgress(progress));
      });
    }
  }, [isLoggedIn]);

  const getProgress = () => {
    const progressToSet = calcProgress(media);
    setProgress(progressToSet);
  };

  useEffect(() => {
    getProgress();
  }, []);

  if (!media) {
    return null;
  }

  return (
    <LinkWithLocale href={RouteHelper.goToPlayer(media)}>
      <a
        className={cx(styles.item, {
          [styles.notPlayable]: !canWatch || !media.IsPlayable,
        })}
      >
        <div className={styles.imageContainer}>
          <ImageWithPlaceholder alt={media.Title} src={imageUrl} />

          <PlayInfo media={media}>
            <PlayFilledIcon className={styles.play} />
          </PlayInfo>

          {!!progress && <ProgressBar progress={progress} />}
        </div>

        <div className={styles.info}>
          <h3 className={styles.title}>
            {`${media.OrderInParent}. ${media.Title}`}
          </h3>

          <h4 className={styles.duration}>
            {TimeHelper.formatDuration(media.Duration)}
          </h4>

          <p className={styles.description}>
            {media.IsPlayable
              ? media.LongDescription
              : t("web_asset_page_avaliable_from", {
                  date: TimeHelper.format(
                    media.AvailableFrom ?? 0,
                    "DD. MMMM YYYY"
                  )?.toLowerCase(),
                })}
          </p>
        </div>
      </a>
    </LinkWithLocale>
  );
};

export default EpisodeListItem;
